import { FC, useMemo, useState } from 'react';
import {
  Button,
  Spinner,
  Alert
} from '@aidkitorg/component-library';
import { useLocalizedStrings } from '../../../Localization';
import { ConversationAreaProps } from './shared/types';
import { MessageBubble } from './components/MessageBubble';
import { ReplyBox } from './components/ReplyBox';
import { buildChannelText } from './shared/utils';
import { useDelayedSpinner, useAutoScroll } from './shared/hooks';

export const ConversationArea: FC<ConversationAreaProps> = ({
  caseInfo,
  messages,
  unhandledCount,
  onResolveAllUnhandled,
  onSendMessage,
  applicantInfo,
  isLoading,
  error,
}) => {
  const L = useLocalizedStrings();
  const [resolvingAll, setResolvingAll] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  // Use custom hooks from shared
  const showSpinner = useDelayedSpinner(isLoading, 300);
  const messagesContainerRef = useAutoScroll(messages, isLoading);

  // Build "Via [channels]" text using shared utility
  const channelText = useMemo(() => buildChannelText(messages, L), [messages, L]);

  const handleResolveAll = async () => {
    setResolvingAll(true);
    try {
      await onResolveAllUnhandled();
    } finally {
      setResolvingAll(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Conversation Header */}
      <div className="bg-white border-b p-3">
        <div className="flex items-stretch justify-between gap-3">
          <div className="flex flex-col justify-center">
            <div className="text-md font-semibold">
              {L.support.conversation_with.replace("{}", applicantInfo?.["legal_name"] || caseInfo.contact)}
            </div>
            <div className="text-sm text-gray-500 mt-0.5">{channelText}</div>
          </div>
          <Button
            variant="secondary"
            className="self-stretch"
            onClick={handleResolveAll}
            disabled={unhandledCount === 0 || resolvingAll}
          >
            {L.support.mark_all_unhandled_as_handled}
          </Button>
        </div>
      </div>

      {/* Yellow Reminder Banner */}
      {!bannerDismissed && (
        <Alert dismissible={true} variant="warning" className="max-w-none">
          <div className="flex-1">
            <p className="text-sm text-gray-900 font-semibold mb-2">{L.applicant.comms.reminder_staff}</p>
            <p className="text-sm text-gray-900 mb-2">{L.applicant.comms.important_obtain_explicit_consent}</p>
            <p className="text-sm text-gray-900 font-semibold mb-1">{L.applicant.comms.example_consent}</p>
            <p className="text-sm text-gray-500 italic mb-0">{L.applicant.comms.before_we_begin}</p>
          </div>
        </Alert>
      )}

      {/* Messages Container */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 bg-white">
        {isLoading ? (
          showSpinner ? (
            <Spinner />
          ) : null
        ) : error ? (
          <div className="text-center text-red-500 mt-8">{L.applicant.comms.error_loading_messages}</div>
        ) : messages.length === 0 ? (
          <div className="text-center text-gray-500 mt-8">
            {L.applicant.comms.no_messages_have_been_sent_or_received}
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message, index) => (
              <MessageBubble
                key={message.id}
                message={message}
                isLast={index === messages.length - 1}
                applicantInfo={applicantInfo}
              />
            ))}
          </div>
        )}
      </div>

      {/* Reply Box */}
      {isLoading ? (
        <div className="bg-white border-t-2 h-[200px]" />
      ) : (
        <ReplyBox onSendMessage={onSendMessage} applicantInfo={applicantInfo} caseInfo={caseInfo} messages={messages} />
      )}
    </div>
  );
};
